import type { LinkProps } from "@/i18n/navigation";
import type { IArticleAuthors } from "@components/core/ArticleAuthorsList";
import type { AspectRatioKey } from "@components/core/AspectRatio";
import type { VariantProps } from "class-variance-authority";
import type { HTMLAttributeAnchorTarget } from "react";
import type * as React from "react";

import { AspectRatio } from "@components/core/AspectRatio";
import { Avatar, type IAvatar } from "@components/core/Avatar";
import { Badge } from "@components/core/Badge";
import { cn } from "@ui/lib/utils";
import { cva } from "class-variance-authority";

import { Link } from "../core/Link";

const cardVariants = cva("relative", {
  variants: {
    withLink: {
      true: "cursor-pointer relative",
      false: "relative",
    },
    shadowHover: {
      true: "hover:shadow-xs transition-shadow duration-300 ease-in-out",
      false: "",
    },
  },
});

export interface ICard {
  className?: string;
  tag?: "article" | "div";
}

interface CardProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof cardVariants>, ICard {
  ref?: React.RefObject<HTMLDivElement>;
}

const Card = ({ tag = "article", shadowHover = false, withLink, className, ref, ...props }: CardProps) => {
  const Tag = tag || "article";
  return <Tag className={cn("card", cardVariants({ withLink, className, shadowHover }))} ref={ref} {...props} />;
};
Card.displayName = "Card";

export const cardLinkVariants = cva("", {
  variants: {
    linkCoverCard: {
      true: [
        "before:absolute before:inset-0 before:content-[''] focus:outline-none",
        "before:rounded-xxl before:border-2 before:border-darkest before:opacity-0",
        "focus:outline-none focus-visible:before:-inset-4 focus-visible:before:opacity-100",
        "before:z-1",
      ],
      false: "effect-focus",
    },
    extraFocusSpace: {
      true: "focus-visible:before:-bottom-8",
      false: "",
    },
  },
});

export interface ICardLink {
  href?: null | string;
  rel?: string;
  target?: HTMLAttributeAnchorTarget;
}

interface CardLinkProps extends Omit<LinkProps, "href">, VariantProps<typeof cardLinkVariants>, ICardLink {
  children: React.ReactNode;
  className?: string;
}

const CardLink = ({ linkCoverCard = true, extraFocusSpace = false, href, className, ...props }: CardLinkProps) =>
  href && (
    <Link
      className={cn(cardLinkVariants({ linkCoverCard, extraFocusSpace, className }))}
      href={href}
      {...props}
      variant='clean'
    />
  );

export interface ICardAsset {
  asset?: React.ReactNode;
  className?: string;
  contained?: boolean;
  hoverEffect?: boolean;
  label?: null | string;
  ratio?: Extract<AspectRatioKey, "1x1" | "16x9" | "responsive" | "responsiveMD">;
  withBorder?: boolean;
  withPlaceholder?: boolean;
}

interface CardAssetProps extends React.HTMLAttributes<HTMLDivElement>, ICardAsset {
  ref?: React.Ref<HTMLDivElement | null>;
}

const CardAsset = ({
  label,
  ratio,
  withPlaceholder,
  contained,
  hoverEffect,
  withBorder,
  asset,
  children,
  ref,
  className,
}: CardAssetProps) => {
  return (
    <AspectRatio
      border={withBorder}
      className={cn(`card-asset relative`, withPlaceholder ? "bg-dark" : "", className)}
      ratio={ratio}
      ref={ref}
    >
      {label && (
        <Badge className='absolute left-12 top-12 z-2' variant='inverse'>
          {label}
        </Badge>
      )}
      {asset && (
        <div
          className={`relative flex h-full w-full items-center justify-center ${hoverEffect && "effect-zoom-hover"}`}
        >
          {contained && (
            <div className='absolute inset-0 opacity-50 blur-md' role='presentation'>
              {asset}
            </div>
          )}
          <div className={cn("w-full", contained ? "rounded-md px-20 lg:px-10 [&>img]:rounded-md" : "h-full")}>
            {asset}
          </div>
        </div>
      )}
      {children}
    </AspectRatio>
  );
};
CardAsset.displayName = "CardAsset";

export interface ICardAuthors extends Partial<IArticleAuthors> {}

export interface ICardAvatar {
  avatar?: IAvatar["image"] | null;
  handle?: null | string;
  name?: null | string;
  smallHandle?: boolean;
}

interface CardAvatarProps extends ICardAvatar {
  className?: string;
}

const CardAvatar = ({ name, avatar, className, handle, smallHandle = false }: CardAvatarProps) => {
  return (
    (avatar || name) && (
      <div className={cn("card-avatar flex items-center gap-x-8", className)}>
        {avatar && <Avatar className='flex-none' image={avatar} size={handle ? "md" : "sm"} />}
        {name && (
          <div>
            <p className={handle ? (smallHandle ? "f-description-03" : "f-ui-03 uppercase") : "f-ui-02"}>{name}</p>
            {handle && (
              <p className={cn("text-lighter", smallHandle ? "f-description-03" : "f-ui-03 mt-4 uppercase")}>
                {`${handle}`}
              </p>
            )}
          </div>
        )}
      </div>
    )
  );
};

CardAvatar.displayName = "CardAvatar";

export { Card, CardAsset, CardAvatar, CardLink };
